import { useCallback } from 'react'
import type { ZodError, ZodTypeAny } from 'zod'
import type { FieldValues, ResolverResult } from 'react-hook-form'

/**
 * Creates a custom React Hook Form resolver using a Zod schema.
 *
 * This resolver is compatible with both full and partial schemas — useful for
 * multi-step forms or conditionally validated sections.
 *
 * @template TFieldValues - The full shape of the form data, usually inferred using `z.infer<typeof schema>`.
 * @param schema - A Zod schema (can be full or partial) used to validate the form step.
 *
 * @returns A memoized resolver function that can be passed to `useForm({ resolver })`.
 */
export const useZodValidationResolver = <TFieldValues extends FieldValues>(
	// Accept any kind of schema so we can use partial schemas
	schema: ZodTypeAny | undefined,
) =>
	useCallback(
		async (
			data: Partial<TFieldValues>,
		): Promise<ResolverResult<TFieldValues>> => {
			try {
				if (typeof schema !== 'undefined') {
					const result = await schema.parseAsync(data)
					return {
						values: result,
						errors: {},
					}
				}
				return {
					values: data,
					errors: {},
				}
			} catch (error) {
				const zodError = error as ZodError
				return {
					values: {},
					errors: zodError.errors.reduce((allErrors, currentError) => {
						const path = currentError.path[0]
						if (typeof path === 'string') {
							allErrors[path] = {
								type: currentError.code,
								message: currentError.message,
							}
						}
						return allErrors
					}, {} as ResolverResult<TFieldValues>['errors']),
				}
			}
		},
		[schema],
	)
